/**
 * Users / Tutors Service — NC-594 (list) + NC-595 (role update).
 *
 * Admin-only surface kept separate from runsApi so the access boundary is
 * easy to grep. Reuses the shared apiClient so 401/403/5xx flow through the
 * existing global error interceptor.
 */

import apiClient from './api';
import { ApiResponse, TutorListItem } from './types';

export type TutorRole = 'viewer' | 'operator' | 'admin';

export const usersApi = {
  /**
   * Admin-only: list all tutors (Azure AD users with portal-side roles).
   * BE enforces RBAC; a non-admin caller receives 403 which the global
   * error interceptor surfaces as a toast.
   */
  async list(): Promise<TutorListItem[]> {
    const response = await apiClient.get<ApiResponse<TutorListItem[]>>('/tutors');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch tutors');
    }
    return response.data.data;
  },

  /**
   * NC-595: Admin-only role assignment (PUT /api/tutors/{userId}). BE has
   * upsert semantics — known userId updates, unknown inserts — but the
   * Role-Editor only edits existing rows, so callers can treat this as an
   * update API. Returns nothing: the caller has already applied the
   * optimistic UI update, and the BE response shape (`Tutor`) is a
   * superset of `TutorListItem`, so re-mapping it here would be churn.
   * Rolls back via thrown Error on any non-success status.
   *
   * BE-enforced guard: a sole-admin self-demote (admin → not-admin on the
   * caller's own row) raises 400 — surface that as a snackbar at the
   * caller (the global toast already handles it, but a domain-specific
   * snackbar is friendlier).
   */
  async updateTutorRole(
    userId: string,
    role: TutorRole,
    options: { displayName?: string } = {}
  ): Promise<void> {
    const body: Record<string, string> = { role };
    if (options.displayName) body.displayName = options.displayName;
    const response = await apiClient.put<ApiResponse<unknown>>(
      `/tutors/${encodeURIComponent(userId)}`,
      body
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update tutor role');
    }
  },

  /**
   * NC-1071: Admin-only soft-delete toggle. Deactivating a tutor keeps the doc
   * (so run-history references survive) but blocks the account at the auth gate
   * and dims the row. The same PUT /api/tutors/{userId} endpoint carries it; the
   * BE bodySchema requires `role`, so the caller passes the tutor's current role
   * unchanged alongside the new `isActive`. The BE rejects self-deactivation (400).
   */
  async setTutorActive(
    userId: string,
    role: TutorRole,
    isActive: boolean
  ): Promise<void> {
    const response = await apiClient.put<ApiResponse<unknown>>(
      `/tutors/${encodeURIComponent(userId)}`,
      { role, isActive }
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update tutor status');
    }
  },

  /**
   * NC-635: Admin-only tutor creation. Targets the same PUT /api/tutors/{userId}
   * endpoint which has upsert semantics — an unknown userId triggers an insert
   * that captures `email` and `displayName` (email is honored on insert and
   * ignored on subsequent updates per tutors-update/index.ts).
   *
   * userId must be the AAD Object ID — authMiddleware looks the tutor up by
   * principal.userId (AAD oid) at sign-in. An email-derived id would be
   * unreachable at auth time.
   */
  async createTutor(input: {
    userId: string;
    displayName: string;
    email: string;
    role: TutorRole;
  }): Promise<void> {
    const response = await apiClient.put<ApiResponse<unknown>>(
      `/tutors/${encodeURIComponent(input.userId)}`,
      { role: input.role, displayName: input.displayName, email: input.email }
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create tutor');
    }
  },
};
